/**
 * useDirectorList
 * Lettaga DB에서 해당 entity의 Officer 목록을 조회하는 composable.
 * positionGroup 파라미터로 director / manager / secretary 구분.
 * 기존 useSsmDirectorFetch.ts 대체 — SSM 크레덴셜 불필요.
 */
import { ref } from 'vue';
import { entityMyApi } from '@/features/Entity_my/api/entityApi';
import type { EntityOfficerAuto, EntityOfficerManual } from '@/features/Entity_my/types/entity.types';

export type OfficerPositionGroup = 'director' | 'manager' | 'secretary';

export interface LettagaDirector {
  officerIdx: string;
  contactCd: string;
  contactRegNo: string;
  fullName: string;
  positionName: string;
  appointmentDate: string;
  cessationDate: string;
  nationalityDisplay: string;
  gender: string;
  race: string;
  designation: string;
  source: 'auto' | 'manual';
}

function mapAutoOfficer(o: EntityOfficerAuto): LettagaDirector {
  return {
    officerIdx: o.id || '',
    contactCd: o.contactCode || '',
    contactRegNo: o.contactRegNo || '',
    fullName: o.fullName || '',
    positionName: o.positionName || '',
    appointmentDate: o.appointmentDate || '',
    cessationDate: o.cessationDate || '',
    nationalityDisplay: o.contactNationalityDisplay || '',
    gender: o.contactProfileGender || '',
    race: o.contactProfileRace || '',
    designation: o.contactProfileDesignation || '',
    source: 'auto',
  };
}

function mapManualOfficer(o: EntityOfficerManual): LettagaDirector {
  return {
    officerIdx: o.id || '',
    contactCd: o.contactCode || '',
    contactRegNo: o.contactRegNo || '',
    fullName: o.fullName || '',
    positionName: o.positionName || '',
    appointmentDate: o.appointmentDate || '',
    cessationDate: '',
    nationalityDisplay: o.contactNationalityDisplay || '',
    gender: o.contactProfileGender || '',
    race: o.contactProfileRace || '',
    designation: o.contactProfileDesignation || '',
    source: 'manual',
  };
}

export function useDirectorList() {
  const directors = ref<LettagaDirector[]>([]);
  const isLoading = ref(false);
  const isFetched = ref(false);
  const errorMessage = ref('');
  const currentPositionGroup = ref<OfficerPositionGroup>('director');

  async function fetchDirectors(entityCd: string, positionGroup: OfficerPositionGroup = 'director') {
    if (!entityCd) return;

    isLoading.value = true;
    isFetched.value = false;
    errorMessage.value = '';
    directors.value = [];
    currentPositionGroup.value = positionGroup;

    try {
      const response: any = await entityMyApi.getEntityOfficer(entityCd, { positionGroup });
      const data = response?.data || response;

      const autoOfficers: EntityOfficerAuto[] = data?.autoOfficers || [];
      const manualOfficers: EntityOfficerManual[] = data?.manualOfficers || [];

      const mapped: LettagaDirector[] = [
        ...autoOfficers.map(mapAutoOfficer),
        ...manualOfficers.map(mapManualOfficer),
      ];

      directors.value = mapped;
      isFetched.value = true;
    } catch (err: any) {
      errorMessage.value = err?.message || 'Failed to fetch officer list.';
      console.error('[useDirectorList] Error:', err);
    } finally {
      isLoading.value = false;
    }
  }

  function reset() {
    directors.value = [];
    isLoading.value = false;
    isFetched.value = false;
    errorMessage.value = '';
    currentPositionGroup.value = 'director';
  }

  return {
    directors,
    isLoading,
    isFetched,
    errorMessage,
    currentPositionGroup,
    fetchDirectors,
    reset,
  };
}
